import React, { useEffect, useState } from 'react';
import { find } from 'lodash';
import QRCode from 'qrcode.react';
import Lottie from 'lottie-react';

import Button from '@material-ui/core/Button';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import DialpadIcon from '@material-ui/icons/Dialpad';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import WifiOffIcon from '@material-ui/icons/WifiOff';

import * as SingleUseCodeService from '../../api/singleUseCodeService';
import * as StampCampaignService from '../../api/stampCampaignService';
import * as LoyaltyProgramService from '../../api/loyaltyProgramService';

import useCompanyContext from '../../hooks/useCompanyContext';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import useResponsiveScreen, {
  getQrSize,
} from '../../hooks/useResponsiveScreen';

import cogs from '../../assets/animations/cogs-work.json';
import { version } from '../../../package.json';
import SelectNumberStampDialog from '../Dialog/SelectNumberStampDialog';
import SelectCardDialog from '../Dialog/SelectCardDialog';
import UnlockPINDialog from '../Dialog/UnlockPINDialog';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
    [theme.breakpoints.down('xs')]: {
      padding: '0 8px',
    },
  },
  logoContainer: {
    marginTop: 90,
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100vw',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      marginTop: 80,
      marginBottom: theme.spacing(2),
    },
  },
  portraitContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '90vh',
    width: '100vw',
  },
  landscapeContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  merchantLogo: {
    height: 'clamp(116px, 40vmin, 384px)',
    width: 'clamp(116px, 40vmin, 384px)',
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  },
  merchantLogoShadow: {
    height: 'clamp(144px, 50vmin, 420px)',
    width: 'clamp(144px, 50vmin, 420px)',
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  },
  merchantLogoPortrait: {
    height: 'clamp(108px, 34vmin, 240px)',
    width: 'clamp(108px, 34vmin, 240px)',
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  },
  logoFallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'uppercase',
  },
  header: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    // Proportional to the screen: scales with vmin (= width in portrait),
    // clamped so it stays readable on phones and bounded on large tablets.
    fontSize: 'clamp(34px, 12vmin, 72px)',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    marginBottom: theme.spacing(4),
  },
  // Compact, fully-fluid header for the landscape start screen, where vertical
  // space is scarce (short landscape heights). Font + margins scale with vmin
  // (= viewport height in landscape) so the logo and "Tap to Collect" subtext
  // below it always stay within the viewport.
  headerLandscapeStart: {
    marginTop: 'clamp(6px, 3vmin, 40px)',
    marginBottom: 'clamp(4px, 1.5vmin, 24px)',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.15,
    // Proportional to the screen: scales with vmin (= height in landscape).
    // Set explicitly rather than inheriting the h1/h2/h4 variant, whose sizes
    // are fixed px in the theme (60/48/28) — at mobile-tier landscape heights
    // that left the name at 28px against a 6vmin subtext, i.e. no hierarchy.
    // Every stop is strictly above the matching stop of `subtext` below the
    // logo (clamp(20px, 6vmin, 48px)) — floor, coefficient and cap all exceed
    // it — so the campaign name outranks the "Tap to Collect" line at every
    // resolution (>=2x), never merely ties. The cap matches
    // `titleHeaderLandscape` so the name doesn't jump size when the QR opens.
    fontSize: 'clamp(34px, 12.5vmin, 104px)',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  cardLabelPortrait: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    fontSize: 'clamp(24px, 8vmin, 56px)',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    marginBottom: theme.spacing(2),
  },
  countdownText: {
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(14px, 2.9vmin, 22px)',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(),
    },
  },
  countdownTextHidden: {
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 18,
    textAlign: 'center',
    color: 'transparent',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(),
      fontSize: 12,
    },
  },
  countdownTextLandscape: {
    marginTop: theme.spacing(),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(17px, 3.1vmin, 29px)',
    textAlign: 'center',
    marginBottom: theme.spacing(),
  },
  countdownTextHiddenLandscapeSmall: {
    marginTop: theme.spacing(),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    textAlign: 'center',
    color: 'transparent',
    marginBottom: theme.spacing(),
  },
  countdownTextHiddenLandscape: {
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 24,
    textAlign: 'center',
    color: 'transparent',
    marginBottom: theme.spacing(2),
  },
  qr: {
    margin: theme.spacing(),
  },
  box: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  refresh: {
    height: 60,
    width: 60,
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
  },
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: 350,
    height: '100%',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(8),
  },
  // Expired-QR box. Sized inline to the same `qrSize` as the live QR so the
  // slot it occupies is identical before and after expiry — the greyed canvas
  // stays in flow (nothing absolute) and the "Expired" label is the only
  // overlay, anchored to this container via `position: relative`.
  qrContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  refreshText: {
    color: theme.palette.grey[600],
    fontSize: 24,
    textAlign: 'center',
    zIndex: 2,
    cursor: 'pointer',
  },
  refreshIcon: {
    height: 200,
    width: 200,
    color: theme.palette.grey[600],
    marginBottom: theme.spacing(1),
    zIndex: 2,
    cursor: 'pointer',
  },
  mainSubtextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'clamp(8px, 3vmin, 32px)',
  },
  mainSubtextPortraitContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  mainSubtextExpiredContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 35,
    width: 350,
  },
  subtext: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(20px, 6vmin, 48px)',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(7),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
  },
  formControl: {
    minWidth: 250,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  containerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  headerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    textAlign: 'center',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  contentLandscape: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  footerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    wordBreak: 'break-word',
    textAlign: 'center',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    marginTop: theme.spacing(2),
  },
  logoContainerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  merchantLogoLandscape: {
    height: 'clamp(120px, 40vmin, 400px)',
    width: 'clamp(120px, 40vmin, 400px)',
    borderRadius: '50%',
    objectFit: 'cover',
    marginLeft: 'clamp(16px, 6vmin, 80px)',
  },
  subtextLandscape: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(24px, 5.2vmin, 42px)',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  grow: {
    height: 16,
    width: 16,
  },
  animationContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  loadingText: {
    marginTop: theme.spacing(),
  },
  buttonLeftContainer: {
    position: 'fixed',
    // Pinned to the very corner; the 90x90 box is kept as the tap target and
    // the icon is aligned into the corner with just enough padding to clear the
    // screen edge (and the iOS home indicator / notch safe areas).
    bottom: 0,
    left: 0,
    width: 90,
    height: 90,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
    paddingLeft: 'max(10px, env(safe-area-inset-left))',
    background: 'transparent',
    boxSizing: 'border-box',
    zIndex: 10,
  },
  buttonRightContainer: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: 90,
    height: 90,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
    paddingRight: 'max(10px, env(safe-area-inset-right))',
    background: 'transparent',
    boxSizing: 'border-box',
    zIndex: 10,
  },
  bottomMenuContainer: {
    width: 250,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    width: 80,
    height: 80,
  },
  greyIcon: {
    color: theme.palette.grey[100],
  },
  noNetworkContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  wifiOff: {
    width: 150,
    height: 150,
  },
  noNetworkHeader: {
    marginBottom: theme.spacing(),
  },
  noNetworkSubText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    textAlign: 'center',
  },
  titleHeaderLandscape: {
    marginTop: theme.spacing(2),
    // Scales with vmin (= height in landscape). Sits well above the
    // `subtextLandscape` line below the QR (clamp(24px, 5.2vmin, 42px)) at
    // every stop — ~2x through the fluid range. The coefficient is held at
    // 10.5 rather than higher because this column also carries the QR, which
    // already claims ~55% of the viewport height on phone-sized landscape.
    fontSize: 'clamp(40px, 10.5vmin, 104px)',
  },
  versionText: {
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grey[100],
    fontSize: 18,
    cursor: 'pointer',
    position: 'fixed',
    top: 12,
    left: 12,
    zIndex: 10,
  },
  expired: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 'clamp(20px, 5vmin, 40px)',
    textAlign: 'center',
    zIndex: 2,
  },
  bottomActionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  topBox: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: 90,
    height: 90,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    paddingTop: 'max(10px, env(safe-area-inset-top))',
    paddingRight: 'max(10px, env(safe-area-inset-right))',
    background: 'transparent',
    boxSizing: 'border-box',
    zIndex: 10,
  },
  cardLabelPortraitContainer: {
    marginBottom: theme.spacing(3),
  },
  subTextPortraitContainer: {
    marginTop: theme.spacing(3),
  },
  cardLabelLandscapeContainer: {
    marginBottom: 'clamp(4px, 2vmin, 24px)',
  },
  subTextLandscapeContainer: {
    marginTop: 'clamp(4px, 2vmin, 24px)',
  },
  icon: {
    padding: 0,
  },
}));

type Props = {
  onLogout: () => void;
  stampNum: number;
  storeId: string;
  onlyScanOnce: boolean;
  maxStampIssued: number;
  stampCampaignList: StampCampaignService.StampCampaign[];
  storeName: string;
};

export default function QRCodeOnlyScreen({
  onLogout,
  stampNum,
  storeId,
  onlyScanOnce,
  maxStampIssued,
  stampCampaignList,
  storeName,
}: Props) {
  const INIT_COUNTDOWN = 90;
  const initStampArray: string[] = ['1', '2', '3', '4', '5', '6', '7', '+'];
  const classes = useStyles();
  const { company } = useCompanyContext();
  const [deeplink, setDeeplink] = useState('');
  const [countdown, setCountdown] = useState(-1);
  const [selectedStamp, setSelectedStamp] = useState(stampNum);
  const [selectOtherNumberOpenDialog, setSelectOtherNumberOpenDialog] =
    useState(false);
  const [stampArray, setStampArray] = useState(initStampArray);
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(
    stampCampaignList.length === 1 ? stampCampaignList[0].slotNumber : 1,
  );
  const [loyaltyProgram, setLoyaltyProgram] =
    useState<LoyaltyProgramService.LoyaltyProgram | null>(null);

  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const isOnline = useNetworkStatus();
  const isCurrentlyOffline =
    isOnline === false ||
    (typeof isOnline !== 'boolean' &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false);
  const { orientation, tier, shortSide } = useResponsiveScreen();
  const isMobile = tier === 'mobile';
  const qrSize = getQrSize(tier, shortSide, orientation);
  const logoSrc = company?.logo?.versions?.xl || '';
  const logoFallbackInitial = (storeName || 'Store').trim().charAt(0) || 'S';
  const shouldShowLogoImage = Boolean(logoSrc) && !logoLoadFailed;
  const [startScreen, setStartScreen] = useState(true);
  const [selectOtherCardOpenDialog, setSelectOtherCardOpenDialog] =
    useState(false);
  const [showMenuPIN, setShowMenuPIN] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [logoSrc]);

  useEffect(() => {
    const fetchLoyaltyProgram = async () => {
      if (company) {
        try {
          const program = await LoyaltyProgramService.get(company.id);
          setLoyaltyProgram(program);
        } catch {
          setLoyaltyProgram(null);
        }
      }
    };
    if (company) {
      fetchLoyaltyProgram();
    }
  }, [company]);

  useEffect(() => {
    const countdownTimer = setTimeout(() => {
      if (countdown > 0) {
        setCountdown(countdown - 1);
      } else {
        setStartScreen(true);
      }
    }, 1000);

    return () => {
      clearTimeout(countdownTimer);
    };
  }, [countdown]);

  // Disable browser zoom gestures while kiosk screen is active.
  useEffect(() => {
    if (pinVerified) {
      return;
    }

    const originalTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = 'none';

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };

    const preventMultiTouch = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    const preventCtrlWheelZoom = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    const nonPassiveOptions = { passive: false } as any;

    document.addEventListener(
      'gesturestart' as any,
      preventGesture as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'gesturechange' as any,
      preventGesture as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'gestureend' as any,
      preventGesture as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'touchmove',
      preventMultiTouch as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'touchend',
      preventDoubleTapZoom as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'wheel',
      preventCtrlWheelZoom as EventListener,
      nonPassiveOptions,
    );

    return () => {
      document.body.style.touchAction = originalTouchAction;
      document.removeEventListener(
        'gesturestart' as any,
        preventGesture as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'gesturechange' as any,
        preventGesture as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'gestureend' as any,
        preventGesture as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'touchmove',
        preventMultiTouch as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'touchend',
        preventDoubleTapZoom as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'wheel',
        preventCtrlWheelZoom as EventListener,
        nonPassiveOptions,
      );
    };
  }, [pinVerified]);

  const generateNewQR = async (stampNum: number) => {
    setIsFetching(true);
    if (company && selectedSlotNumber > 0) {
      try {
        const res = await SingleUseCodeService.generate({
          companyId: company.id,
          numOfStamps: stampNum,
          slotNumber: selectedSlotNumber,
          storeId,
        });
        setDeeplink(res.deepLink);
        setCountdown(INIT_COUNTDOWN);
        setIsFetching(false);
      } catch (e: any) {
        console.log('Error: ', e);
        setIsFetching(false);
        setFetchError(true);
      }
    }
  };

  const onRefresh = (stampNum: number) => {
    setCountdown(0);
    generateNewQR(stampNum);
  };

  const checkIfOnline = () => {
    if (isOnline) {
      window.location.reload();
    }
  };

  if (isCurrentlyOffline) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography
          variant={isMobile ? 'h5' : 'h3'}
          className={classes.noNetworkHeader}
        >
          No Internet Connection
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and try again.
        </Typography>
        <Button variant="contained" color="primary" onClick={checkIfOnline}>
          REFRESH
        </Button>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography
          variant={isMobile ? 'h5' : 'h3'}
          className={classes.noNetworkHeader}
        >
          Network Error
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and tap REFRESH to try again.
        </Typography>
        <Button variant="contained" color="primary" onClick={checkIfOnline}>
          REFRESH
        </Button>
      </div>
    );
  }

  if (!company) {
    return (
      <div className={classes.animationContainer}>
        <Lottie
          animationData={cogs}
          loop={true}
          autoplay={true}
          style={{
            width: 'clamp(220px, 60vmin, 450px)',
            height: 'clamp(260px, 66vmin, 500px)',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <Typography variant="h5" align="center" color="error">
          Unable to load Code Generator screen. Please try refreshing or contact
          support.
        </Typography>
      </div>
    );
  }

  if (isFetching) {
    return (
      <div className={classes.animationContainer}>
        <Lottie
          animationData={cogs}
          loop={true}
          autoplay={true}
          style={{
            width: 'clamp(220px, 60vmin, 450px)',
            height: 'clamp(260px, 66vmin, 500px)',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <Typography
          variant={isMobile ? 'h6' : 'h4'}
          align="center"
          className={classes.loadingText}
        >
          Loading QR code...
        </Typography>
      </div>
    );
  }

  return (
    <>
      {orientation === 'portrait' ? (
        <div className={classes.wrapper}>
          <>
            <Typography
              className={classes.versionText}
              onClick={() => window.location.reload()}
            >
              v{version}
            </Typography>
          </>
          {stampCampaignList.length > 1 && (
            <div
              className={classes.topBox}
              onClick={() => setSelectOtherCardOpenDialog(true)}
            >
              <IconButton
                className={classes.icon}
                onClick={() => setSelectOtherCardOpenDialog(true)}
              >
                <SwapHorizIcon className={classes.greyIcon} />
              </IconButton>
            </div>
          )}
          {startScreen && (
            // Your init state component or logic here
            <>
              <div className={classes.portraitContainer}>
                <div className={classes.cardLabelPortraitContainer}>
                  <Typography variant="h1" className={classes.header}>
                    {
                      find(stampCampaignList, {
                        slotNumber: selectedSlotNumber,
                      })?.name
                    }
                  </Typography>
                </div>
                {shouldShowLogoImage ? (
                  <img
                    src={logoSrc}
                    className={classes.merchantLogoShadow}
                    alt="flex reward"
                    onError={() => setLogoLoadFailed(true)}
                    onClick={() => {
                      setStartScreen(false);
                      setSelectedStamp(1);
                      onRefresh(1);
                    }}
                  />
                ) : (
                  <div
                    className={`${classes.merchantLogo} ${classes.logoFallback}`}
                    aria-label="merchant logo placeholder"
                    onClick={() => {
                      setStartScreen(false);
                      setSelectedStamp(1);
                      onRefresh(1);
                    }}
                  >
                    {logoFallbackInitial}
                  </div>
                )}
                <div
                  className={classes.mainSubtextContainer}
                  onClick={() => {
                    setStartScreen(false);
                    setSelectedStamp(1);
                    onRefresh(1);
                  }}
                >
                  <div className={classes.subTextPortraitContainer}>
                    <Typography className={classes.subtext} variant="h1">
                      Tap to Collect Your Stamp
                    </Typography>
                  </div>
                </div>
              </div>
              <div className={classes.bottomActionContainer}>
                <div
                  className={classes.buttonLeftContainer}
                  onClick={() => {
                    if (!showMenuPIN) {
                      setShowMenuPIN(true);
                    }
                    if (pinVerified) {
                      setDeeplink('');
                      onLogout();
                    }
                  }}
                >
                  <IconButton
                    className={classes.icon}
                    onClick={() => {
                      if (!showMenuPIN) {
                        setShowMenuPIN(true);
                      }
                      if (pinVerified) {
                        setDeeplink('');
                        onLogout();
                      }
                    }}
                  >
                    {!showMenuPIN && !pinVerified && (
                      <LockOutlinedIcon className={classes.greyIcon} />
                    )}
                    {pinVerified && (
                      <ExitToAppIcon className={classes.greyIcon} />
                    )}
                  </IconButton>
                </div>
                {maxStampIssued > 1 && (
                  <div
                    className={classes.buttonRightContainer}
                    onClick={() => {
                      setSelectOtherNumberOpenDialog(true);
                    }}
                  >
                    <IconButton
                      className={classes.icon}
                      onClick={() => {
                        setSelectOtherNumberOpenDialog(true);
                      }}
                    >
                      <DialpadIcon className={classes.greyIcon} />
                    </IconButton>
                  </div>
                )}
              </div>
            </>
          )}
          {!startScreen && (
            <>
              <div className={classes.portraitContainer}>
                {shouldShowLogoImage ? (
                  <img
                    src={logoSrc}
                    className={classes.merchantLogoPortrait}
                    alt="flex reward"
                    onError={() => setLogoLoadFailed(true)}
                    onClick={() => {
                      setSelectedStamp(1);
                      onRefresh(1);
                    }}
                  />
                ) : (
                  <div
                    className={`${classes.merchantLogoPortrait} ${classes.logoFallback}`}
                    aria-label="merchant logo placeholder"
                    onClick={() => {
                      setSelectedStamp(1);
                      onRefresh(1);
                    }}
                  >
                    {logoFallbackInitial}
                  </div>
                )}
                <Typography
                  variant={isMobile ? 'h4' : 'h2'}
                  className={classes.cardLabelPortrait}
                >
                  {
                    find(stampCampaignList, {
                      slotNumber: selectedSlotNumber,
                    })?.name
                  }
                </Typography>
                <Typography variant="body1" className={classes.countdownText}>
                  {countdown.toString()} second{countdown > 1 ? 's' : ''}{' '}
                  remaining
                </Typography>
                <div className={classes.qr}>
                  {countdown === 0 ? (
                    <div
                      className={classes.qrContainer}
                      style={{ width: qrSize, height: qrSize }}
                    >
                      <QRCode
                        value={deeplink}
                        size={qrSize}
                        renderAs="canvas"
                        includeMargin
                        fgColor={'#e1e1e1'}
                      />
                      <Typography className={classes.expired}>
                        Expired
                      </Typography>
                    </div>
                  ) : (
                    <QRCode
                      value={deeplink}
                      size={qrSize}
                      renderAs="svg"
                      includeMargin
                    />
                  )}
                </div>
                <div className={classes.mainSubtextPortraitContainer}>
                  <Typography
                    className={classes.subtext}
                    variant={isMobile ? 'h5' : 'h3'}
                  >
                    Scan for {selectedStamp.toString()} Stamp
                    {selectedStamp > 1 ? 's' : ''}
                  </Typography>
                </div>
              </div>
              <div className={classes.bottomActionContainer}>
                <div
                  className={classes.buttonLeftContainer}
                  onClick={() => {
                    if (!showMenuPIN) {
                      setShowMenuPIN(true);
                    }
                    if (pinVerified) {
                      setDeeplink('');
                      onLogout();
                    }
                  }}
                >
                  <IconButton
                    onClick={() => {
                      if (!showMenuPIN) {
                        setShowMenuPIN(true);
                      }
                      if (pinVerified) {
                        setDeeplink('');
                        onLogout();
                      }
                    }}
                  >
                    {!showMenuPIN && !pinVerified && (
                      <LockOutlinedIcon className={classes.greyIcon} />
                    )}
                    {pinVerified && (
                      <ExitToAppIcon className={classes.greyIcon} />
                    )}
                  </IconButton>
                </div>
                <Typography
                  className={classes.versionText}
                  onClick={() => window.location.reload()}
                >
                  v{version}
                </Typography>
                {maxStampIssued > 1 && (
                  <div
                    className={classes.buttonRightContainer}
                    onClick={() => {
                      setSelectOtherNumberOpenDialog(true);
                    }}
                  >
                    <IconButton
                      className={classes.icon}
                      onClick={() => {
                        setSelectOtherNumberOpenDialog(true);
                      }}
                    >
                      <DialpadIcon className={classes.greyIcon} />
                    </IconButton>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={classes.wrapper}>
          <>
            <Typography
              className={classes.versionText}
              onClick={() => window.location.reload()}
            >
              v{version}
            </Typography>
          </>
          {stampCampaignList.length > 1 && (
            <div
              className={classes.topBox}
              onClick={() => setSelectOtherCardOpenDialog(true)}
            >
              <IconButton
                className={classes.icon}
                onClick={() => setSelectOtherCardOpenDialog(true)}
              >
                <SwapHorizIcon className={classes.greyIcon} />
              </IconButton>
            </div>
          )}
          {startScreen && (
            // Your init state component or logic here
            <>
              <div className={classes.landscapeContainer}>
                <div className={classes.cardLabelLandscapeContainer}>
                  <Typography
                    variant={
                      tier === 'standardTablet'
                        ? 'h1'
                        : tier === 'compactTablet'
                          ? 'h2'
                          : 'h4'
                    }
                    className={classes.headerLandscapeStart}
                  >
                    {
                      find(stampCampaignList, {
                        slotNumber: selectedSlotNumber,
                      })?.name
                    }
                  </Typography>
                </div>
                {shouldShowLogoImage ? (
                  <img
                    src={logoSrc}
                    className={classes.merchantLogo}
                    alt="flex reward"
                    onError={() => setLogoLoadFailed(true)}
                    onClick={() => {
                      setStartScreen(false);
                      setSelectedStamp(1);
                      onRefresh(1);
                    }}
                  />
                ) : (
                  <div
                    className={`${classes.merchantLogo} ${classes.logoFallback}`}
                    aria-label="merchant logo placeholder"
                    onClick={() => {
                      setStartScreen(false);
                      setSelectedStamp(1);
                      onRefresh(1);
                    }}
                  >
                    {logoFallbackInitial}
                  </div>
                )}
                <div
                  className={classes.mainSubtextContainer}
                  onClick={() => {
                    setStartScreen(false);
                    setSelectedStamp(1);
                    onRefresh(1);
                  }}
                >
                  <div className={classes.subTextLandscapeContainer}>
                    <Typography className={classes.subtext} variant="h1">
                      Tap to Collect Your Stamp
                    </Typography>
                  </div>
                </div>
              </div>
            </>
          )}
          {!startScreen && (
            <>
              <div className={classes.containerLandscape}>
                <div className={classes.headerLandscape}>
                  <Typography
                    variant={
                      tier === 'standardTablet'
                        ? 'h1'
                        : tier === 'compactTablet'
                          ? 'h2'
                          : 'h4'
                    }
                    className={classes.titleHeaderLandscape}
                  >
                    {
                      find(stampCampaignList, {
                        slotNumber: selectedSlotNumber,
                      })?.name
                    }
                  </Typography>
                  {countdown >= 0 && (
                    <Typography className={classes.countdownTextLandscape}>
                      {countdown.toString()} second{countdown > 1 ? 's' : ''}{' '}
                      remaining ...
                    </Typography>
                  )}
                </div>
                <div className={classes.contentLandscape}>
                  <div className={classes.qr}>
                    {countdown <= 0 ? (
                      <div
                        className={classes.qrContainer}
                        style={{ width: qrSize, height: qrSize }}
                      >
                        <QRCode
                          value={deeplink}
                          size={qrSize}
                          renderAs="canvas"
                          fgColor={'#e1e1e1'}
                        />
                        <Typography className={classes.expired}>
                          Expired
                        </Typography>
                      </div>
                    ) : (
                      <QRCode value={deeplink} size={qrSize} renderAs="svg" />
                    )}
                  </div>
                  <div className={classes.logoContainerLandscape}>
                    {shouldShowLogoImage ? (
                      <img
                        src={logoSrc}
                        className={classes.merchantLogoLandscape}
                        style={{ width: qrSize, height: qrSize }}
                        alt="flex reward"
                        onError={() => setLogoLoadFailed(true)}
                        onClick={() => {
                          setSelectedStamp(1);
                          onRefresh(1);
                        }}
                      />
                    ) : (
                      <div
                        className={`${classes.merchantLogoLandscape} ${classes.logoFallback}`}
                        style={{ width: qrSize, height: qrSize }}
                        aria-label="merchant logo placeholder"
                        onClick={() => {
                          setSelectedStamp(1);
                          onRefresh(1);
                        }}
                      >
                        {logoFallbackInitial}
                      </div>
                    )}
                  </div>
                </div>
                <div className={classes.footerLandscape}>
                  <Typography className={classes.subtextLandscape}>
                    Scan for {selectedStamp.toString()} Stamp
                    {selectedStamp > 1 ? 's' : ''}
                  </Typography>
                </div>
              </div>
            </>
          )}
          <div className={classes.bottomActionContainer}>
            <div className={classes.buttonLeftContainer}>
              <IconButton
                className={classes.icon}
                onClick={() => {
                  if (!showMenuPIN) {
                    setShowMenuPIN(true);
                  }
                  if (pinVerified) {
                    setDeeplink('');
                    onLogout();
                  }
                }}
              >
                {!showMenuPIN && !pinVerified && (
                  <LockOutlinedIcon className={classes.greyIcon} />
                )}
                {pinVerified && <ExitToAppIcon className={classes.greyIcon} />}
              </IconButton>
            </div>
            {maxStampIssued > 1 && (
              <div
                className={classes.buttonRightContainer}
                onClick={() => {
                  setSelectOtherNumberOpenDialog(true);
                }}
              >
                <IconButton
                  className={classes.icon}
                  onClick={() => {
                    setSelectOtherNumberOpenDialog(true);
                  }}
                >
                  <DialpadIcon className={classes.greyIcon} />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      )}
      <SelectCardDialog
        open={selectOtherCardOpenDialog}
        onClose={() => setSelectOtherCardOpenDialog(false)}
        confirmSelectedSlotNumber={(slotNum: number) => {
          setCountdown(0);
          setSelectedSlotNumber(slotNum);
          setSelectOtherCardOpenDialog(false);
          setSelectedStamp(1);
          onRefresh(1);
        }}
        stampCampaignList={stampCampaignList}
        currentSelectedSlotNumber={selectedSlotNumber}
      />
      <SelectNumberStampDialog
        open={selectOtherNumberOpenDialog}
        onClose={() => setSelectOtherNumberOpenDialog(false)}
        confirmSelectedNumber={(selectNum: string) => {
          if (startScreen) {
            setStartScreen(false);
          }
          setCountdown(0);
          let updateStampArray = stampArray;
          updateStampArray.pop();
          updateStampArray.push(selectNum);
          setStampArray(updateStampArray);
          setSelectedStamp(parseInt(selectNum, 10));
          setSelectOtherNumberOpenDialog(false);
          onRefresh(parseInt(selectNum, 10));
        }}
        maxSelectedNumber={maxStampIssued}
        maxStampIssued={maxStampIssued}
        onlyScanOnce={onlyScanOnce}
        stampNum={stampNum}
      />
      {loyaltyProgram && showMenuPIN && (
        <UnlockPINDialog
          open={showMenuPIN}
          onClose={() => setShowMenuPIN(false)}
          onSuccess={() => {
            setPinVerified(true);
            setShowMenuPIN(false);
          }}
          menuPIN={loyaltyProgram.menuUnlockPin}
        />
      )}
    </>
  );
}
